import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { submitContactApi } from '@/lib/api'
import { useDetectedCountry } from '@/hooks/useDetectedCountry'
import { toast } from 'sonner'
import { CheckCircleIcon } from 'lucide-react'

const inputClass =
  'border-white/20 bg-white/5 text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30'

export const Route = createFileRoute('/contact')({
  component: ContactPage,
})

function ContactPage() {
  const { country } = useDetectedCountry()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      await submitContactApi({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        country_code: country?.iso_2,
      })
      setSubmitted(true)
      toast.success('Message sent! Our support team will be in touch shortly.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-svh overflow-hidden bg-[#0B1120]">
      {/* Decorative glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 size-[600px] rounded-full bg-[#00E5A0]/8 blur-[140px]" />

      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="mx-auto max-w-2xl px-6 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#00E5A0] transition-colors"
          >
            ← Back to home
          </Link>

          <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight tracking-tight text-white">
            Contact us
          </h1>
          <p className="mt-4 text-base text-white/60 leading-relaxed">
            Have a question about PeerPass? Send us a message and our support team will get back to
            you.
          </p>

          {submitted ? (
            <div className="mt-10 rounded-2xl border border-[#00E5A0]/30 bg-[#00E5A0]/10 p-8 text-center">
              <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-[#00E5A0] text-[#0B1120]">
                <CheckCircleIcon className="size-7" />
              </div>
              <h2 className="mt-4 text-xl font-semibold text-white">Message sent!</h2>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                Thank you, {name.split(' ')[0]}. Our support team will reply to {email} shortly.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false)
                  setName('')
                  setEmail('')
                  setSubject('')
                  setMessage('')
                }}
                className="mt-6 text-sm text-[#00E5A0] underline underline-offset-4 hover:text-[#00E5A0]/80 transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-10 space-y-5" aria-label="Contact form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-white/80">
                    Name <span className="text-[#00E5A0]">*</span>
                  </Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    disabled={loading}
                    placeholder="Jane Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-white/80">
                    Email <span className="text-[#00E5A0]">*</span>
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    disabled={loading}
                    placeholder="jane@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-subject" className="text-white/80">
                  Subject <span className="text-[#00E5A0]">*</span>
                </Label>
                <Input
                  id="contact-subject"
                  type="text"
                  required
                  disabled={loading}
                  placeholder="How does payroll deduction work?"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message" className="text-white/80">
                  Message <span className="text-[#00E5A0]">*</span>
                </Label>
                <Textarea
                  id="contact-message"
                  rows={5}
                  required
                  disabled={loading}
                  placeholder="Tell us how we can help…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  size="lg"
                  className="w-full sm:w-auto bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                >
                  {loading ? 'Sending…' : 'Send message'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-10 sm:flex-row sm:px-8 lg:px-12">
          <p className="text-xs text-white/30">
            &copy; {new Date().getFullYear()} PeerPass. All rights reserved.
          </p>
          <Link to="/" className="text-xs text-white/40 hover:text-white/70 transition-colors">
            Back to home
          </Link>
        </div>
      </footer>
    </div>
  )
}